// Справочник и остатки: то, что оболочка спрашивает сразу после входа.
//
// Тарифы, лимиты и склонения единиц приходят с сервера, а не зашиты в клиент —
// иначе смена лимита в админке требовала бы релиза (SMY-57).

import type { Balance, EditPolicy, ScenarioId, TariffId, UnitId, UnitLabel, UnitScope } from '@smyslika/core'

import type { Client } from './client.ts'

export interface ReferenceTariff {
  id: TariffId
  /**
   * Название тарифа. Именно `label`, а не `title`: так его зовёт `TARIFFS` в
   * ядре, и ответ сервера собирается расплывом оттуда. Пока здесь стояло
   * `title`, типы обещали поле, которого в ответе нет вовсе, — и экран 10,
   * написанный по этим типам, показал бы пустое место вместо «Плюс».
   */
  label: string
  price: number
  speed: string
  limits: Record<UnitId, number>
}

/**
 * Единица — только счёт и склонения.
 *
 * Объём и состав результата отсюда уехали к сценариям (06.09.2026): вопрос,
 * поиск и текст списываются одной единицей, а обещают разное — «7 источников
 * со ссылками» из слова «вопрос» не выводится.
 */
export interface ReferenceUnit {
  id: UnitId
  labels: UnitLabel
}

/** Сценарий: чем списывается, что входит и запустится ли он сегодня. */
export interface ReferenceScenario {
  id: ScenarioId
  /**
   * Единица списания. Приходит с сервера, а не выводится клиентом:
   * соответствие «сценарий → единица» живёт в конфигурации и меняется из
   * админки без релиза.
   */
  unit: UnitId
  /** Объём одного результата: сколько источников, утверждений, вариантов. */
  scope: UnitScope
  /** Из чего состоит результат — экран 04 обязан назвать, что входит в списание. */
  includes: string[]
  /** Когда именно спишется. Экран 11, «Правила списания единиц». */
  chargedWhen: string
  /**
   * Сколько правок результата бесплатны и как долго.
   *
   * Своё у каждого сценария: у изображения окно в минутах, у документа — три
   * правки без срока. Приходит с сервера, а не считается клиентом: правило
   * меняется из админки без релиза.
   */
  freeEdits: EditPolicy
  /**
   * Запустится ли он на самом деле. Приходит только с сервера, поднятого с
   * базой: без неё доступность выдумывать нечем, и поля просто нет.
   */
  available?: boolean
}

/** Правила входа. Экран 01 берёт их отсюда, а не держит своими числами. */
export interface ReferenceAuth {
  codeLength: number
  resendCooldownSeconds: number
  /**
   * Сколько ждать SMS. Отдельно от паузы повтора: это разные обещания —
   * ждать пять минут, просить новый код через семь. На бесплатном имени
   * отправителя сообщение идёт через модерацию, и без этой строки экран
   * входа выглядит сломанным ровно те минуты, пока код в пути.
   */
  smsDeliverySeconds: number
  /** Минимальная длина пароля: форма гасит кнопку по этому числу. */
  passwordMinLength: number
  /**
   * Те же числа для канала ПИСЕМ. SMY-157
   *
   * ⚠️ Отдельным блоком, а не заменой соседних, и это не переходная
   * неаккуратность: у письма другие сроки по существу — код живёт пятнадцать
   * минут против десяти, повтор открывается через минуту против семи. Общий
   * блок означал бы, что экран показывает человеку срок чужого канала.
   *
   * Необязателен: сервер прошлой версии его не присылает, и форма обязана
   * работать со своими умолчаниями, а не падать.
   */
  email?: {
    codeLength: number
    resendCooldownSeconds: number
    /** Сколько ждать письмо. Минута, а не «секунды»: перед ящиком стоит
     *  greylisting принимающей стороны, придерживающий первое письмо. */
    deliverySeconds: number
  }
}

export interface Reference {
  tariffs: ReferenceTariff[]
  units: ReferenceUnit[]
  /** Что НЕ списывается. Экран 11, «Правила списания единиц». */
  notCharged: readonly string[]
  auth: ReferenceAuth
  /**
   * Сценарии: чем списывается каждый, что входит в результат и запустится ли
   * он на самом деле.
   *
   * Приходит всегда — состав и объём собираются из конфигурации, которая есть
   * и без базы. А вот `available` у каждого может отсутствовать: доступность
   * зависит от реестра workflow, и на сервере без базы выдумывать её нечем.
   */
  scenarios: ReferenceScenario[]
}

/** Остатки на текущий расчётный период. Экраны 02, 04 и 10. */
export interface Balances {
  tariff: TariffId
  source?: string
  since: string
  /** Когда лимиты обнулятся. Экран 10 показывает эту дату прямо. */
  until: string
  tariffUntil?: string
  balances: Balance[]
}

export interface Catalog {
  reference(): Promise<Reference>
  balances(): Promise<Balances>
}

export function createCatalog(client: Client): Catalog {
  return {
    reference: () => client.request<Reference>('/v1/reference', { anonymous: true }),
    balances: () => client.request<Balances>('/v1/balances'),
  }
}
